import { collectErrors, numberInRange, required } from '../../utils/formValidation';

/*
 * An isolate's intake asks one question first: what arrived. The answer
 * decides which fields follow, what POST /external is told (`donation_type`,
 * which is what makes the server mint the isolate), what is sent after it,
 * and so where the isolate starts on Home's pipeline.
 *
 *   tissue      a raw specimen: a fresh field mushroom, a dried voucher, tissue
 *               in silica gel. Its isolate waits for the lab to plate it.
 *   culture     a live agar plate from another lab, an exchange or a project.
 *               POST /plates follows, so it starts as a lab culture.
 *   bank_stock  tubes that are already preserved -- a cryovial, a glycerol
 *               stock, a repository tube (ATCC, CBS). POST
 *               /slants/external-intake follows with their place, so every
 *               tube is banked in the same transaction that registers it.
 *   info_only   a report with no material: no isolate is made, and the
 *               specimen description is the whole record.
 *
 * Everything here is pure: the form (IntakeForm.js) keeps the state, and the
 * payloads are built from it here so the tests can read them field by field.
 */

export const SOURCE_TYPES = ['tissue', 'culture', 'bank_stock', 'info_only'];

/*
 * Per source type:
 *   donationType  what POST /external is told (routes/external.py
 *                 DONATION_TYPES_WITH_MATERIAL names the three that mint)
 *   stage         where the isolate starts, as source.stage.<stage> says it
 *   population    the hub population it lands in (specimen_overview.py), or
 *                 null when no isolate is made
 *   icon          the card's picture; decorative, the title says it
 *   labMaterial   only lab writers may register it: POST /plates and POST
 *                 /slants/external-intake refuse anyone else
 *   material      the section that describes what arrived
 *   description   the specimen description: 'open' (optional, shown),
 *                 'folded' (optional, behind a disclosure) or 'required'
 */
export const SOURCES = {
  tissue: { donationType: 'tissue', stage: 'awaitingIsolation', population: 'awaiting', icon: '🍄',
    labMaterial: false, material: 'condition', description: 'open' },
  culture: { donationType: 'plate', stage: 'labCulture', population: 'active', icon: '🧫',
    labMaterial: true, material: 'plate', description: 'folded' },
  bank_stock: { donationType: 'slant', stage: 'bank', population: 'bank', icon: '🧊',
    labMaterial: true, material: 'bankStock', description: 'folded' },
  info_only: { donationType: 'info_only', stage: 'noIsolate', population: null, icon: '📝',
    labMaterial: false, material: null, description: 'required' },
};

/** The source types this person may register: the lab ones only for a lab writer. */
export const sourcesFor = (canCreateLabMaterial) =>
  SOURCE_TYPES.filter((type) => canCreateLabMaterial || !SOURCES[type].labMaterial);

/* ── the shared intake record ──────────────────────────────────────────── */

export const EMPTY_FORM = {
  qr_uid: '',
  donor_name: '',
  donor_contact: '',
  original_collector: '',
  original_collection_date: '',
  original_location_name: '',
  // The donor's (or collector's) name for it (0110): Home's table shows it.
  given_name: '',
  original_gps_lat: '',
  original_gps_lng: '',
  substrate: '',
  fruiting_body_size: '',
  phenological_stage: '',
  fruiting_body_count: '',
  growth_pattern: '',
  fruiting_body_size_estimated: false,
  taxon_name: '',
  taxonomic_rank: 'species',
  confidence: 'certain',
  general_notes: '',
};

/** The intake's code, from the time it was opened -- the same format
 *  routes/external.py falls back to: EXT-HHMMSSDDMMYYYY. */
export function generateQrUid(now = new Date()) {
  const pad = (n, l = 2) => String(n).padStart(l, '0');
  return `EXT-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getDate())}`
    + `${pad(now.getMonth() + 1)}${now.getFullYear()}`;
}

/** The specimen description's fields, in screen order. A report with no
 *  material must fill every one; for the other types they are optional, and
 *  folded away for a culture or bank stock (IntakeForm opens the fold when
 *  one of them is wrong, so the message is never hidden). */
export const DESCRIPTION_FIELDS = ['substrate', 'fruiting_body_size', 'phenological_stage', 'fruiting_body_count',
  'growth_pattern'];
export const INFO_ONLY_FIELDS = DESCRIPTION_FIELDS;

/** A count of fruiting bodies, when one is given, is a whole number of at
 *  least one. The form does not validate natively (noValidate), so a bad
 *  number is said here, beside its field, rather than silently blocking. */
export const countError = (value) => numberInRange(value, { min: 1, integer: true }, 'source.errors.countInvalid');

/** "Donor name or original collector": blank only when both are. */
export const isBlankPair = (a, b) => !String(a || '').trim() && !String(b || '').trim();

/* ── a raw specimen's condition ────────────────────────────────────────── */

export const TISSUE_CONDITIONS = ['fresh', 'dried', 'silica_gel'];
/** The isolate's preservation_method for each condition: a fresh specimen is
 *  not preserved at all, so it sends none. */
export const CONDITION_METHOD = { fresh: null, dried: 'dried', silica_gel: 'silica_gel' };

/* ── a live culture's plate ────────────────────────────────────────────── */

export const EMPTY_PLATE_SUB = { protocol: '', protocolOther: '', temperature: 25, createControlPair: false, notes: '' };

/** The protocol a plate or slant form names: the typed one when "Other" is chosen. */
export const protocolOf = (sub) => (sub.protocol === 'other' ? (sub.protocolOther || '').trim() : sub.protocol);

/** A typed protocol name to add to the list (POST /protocols), or null. */
export const protocolToRegister = (sub) => (sub.protocol === 'other' && protocolOf(sub) ? protocolOf(sub) : null);

/* The plate a donor's culture goes onto needs a protocol: POST /plates
 * refuses an external_collection plate without one (routes/plates.py). With
 * "Other" chosen, the typed name is what has to be there. */
export const plateSubErrors = (sub, key) => collectErrors([
  sub.protocol === 'other'
    ? ['protocolOther', required(sub.protocolOther, key)]
    : ['protocol', required(sub.protocol, key)],
]);

/** POST /plates for a donor's culture, as an intake record's drawer sends it too. */
export const plateBody = (externalId, sub) => ({
  origin: 'external_collection',
  external_id: externalId,
  protocol: protocolOf(sub),
  temperature_c: parseFloat(sub.temperature) || 25,
  create_control_pair: sub.createControlPair,
  notes: (sub.notes || '').trim() || undefined,
});

/* ── slants registered later (an intake record's drawer) ─────────────── */

export const EMPTY_SLANT_SUB = { entries: [{ code: '', role: null }], protocol: '', protocolOther: '' };
export const defaultSlantSub = (qrUid) => ({ entries: [{ code: qrUid ? `${qrUid}-S1` : '', role: null }],
  protocol: '', protocolOther: '' });

/* POST /slants/external-intake needs at least one slant code; the message
 * goes on the first code field. */
export const slantSubErrors = (sub, key) => collectErrors([
  ['slantCode', sub.entries.some((en) => en.code.trim()) ? null : { code: 'required', key }],
]);

/* ── bank stock: tubes that arrive preserved ───────────────────────────── */

/** Bank stock's preservation methods, the usual one first (routes/specimens.py
 *  PRESERVATION_METHODS, without the two a tube is never kept by; glycerol_30
 *  since 0064, in place of mineral_oil). */
export const BANK_STOCK_METHODS = ['cryopreservation', 'slant', 'glycerol_30', 'water', 'freeze_drying', 'other'];
/** Tubes one request places (routes/slants.py MAX_DEPOSIT). */
export const MAX_TUBES = 20;

// utils.STORAGE_WELL: a row letter or two and a column number of up to three
// digits -- and the column starts at 1 (utils.canonical_well refuses A0).
const WELL = /^([A-Z]{1,2})(\d{1,3})$/;

/** Whether this, as normaliseWell writes it, is a well the server takes. */
const isWell = (well) => {
  const m = WELL.exec(well);
  return !!m && Number(m[2]) > 0;
};

/**
 * A well as the server stores it, one spelling per well: "c 7" is C7, and
 * A01 is A1 -- the server compares them as one place (utils.canonical_well).
 * Anything that is not a well comes back as typed, upper-cased, for the
 * form to refuse.
 */
export const normaliseWell = (well) => {
  const text = String(well ?? '').trim().toUpperCase().replace(/\s+/g, '');
  return isWell(text) ? text.replace(WELL, (_, row, column) => `${row}${Number(column)}`) : text;
};

/**
 * `n` wells in a row from `start`, along the column: 'A1', 3 is A1, A2, A3;
 * 'A01' is A1 too, so A1, A2, A3. Past three digits, or from a start that is
 * not a well, the rest are blank -- nothing is guessed.
 */
export function nextWells(start, n) {
  const well = normaliseWell(start);
  const m = isWell(well) && WELL.exec(well);
  if (!m) return Array.from({ length: n }, () => '');
  const [, row, digits] = m;
  return Array.from({ length: n }, (_, i) => {
    const column = Number(digits) + i;
    return column > 999 ? '' : `${row}${column}`;
  });
}

/** The next tube's well after `previous`, or '' when there is none to follow. */
export const wellAfter = (previous) => nextWells(previous, 2)[1];

/** A tube's suggested code: the intake's code, S and its number. */
export const tubeCode = (qrUid, i) => (qrUid ? `${qrUid}-S${i + 1}` : '');

const codeOf = (entry) => String(entry?.code ?? '').trim();

/**
 * The next tube's suggested code: the lowest `{qr}-S{n}` (n from 1) no tube
 * has yet, compared without case as labels are read. After S1, S2, S3 with
 * S2 removed, the next is S2 again -- not a second S3. '' with no intake code.
 */
export function nextTubeCode(qrUid, entries = []) {
  if (!qrUid) return '';
  const taken = new Set(entries.map((en) => codeOf(en).toLowerCase()));
  for (let i = 0; ; i += 1) {
    const code = tubeCode(qrUid, i);
    if (!taken.has(code.toLowerCase())) return code;
  }
}

/**
 * The tubes after the intake's code changed from `oldQr` to `newQr`. A code
 * that still follows the old one's pattern (`{oldQr}-S{n}`) keeps its number
 * under the new code -- or goes blank while the intake code is cleared -- and
 * a blank code takes the lowest number still free. So clearing the intake
 * code and typing another gives the tubes codes again. A code a person typed
 * themselves is never changed.
 */
export function retagTubes(entries, oldQr, newQr) {
  const prefix = `${oldQr}-S`;
  const follows = (code) => !!oldQr && code.startsWith(prefix) && /^\d+$/.test(code.slice(prefix.length));
  const out = entries.map((en) => {
    const code = String(en.code ?? '');
    if (!follows(code)) return en;
    return { ...en, code: newQr ? `${newQr}-S${code.slice(prefix.length)}` : '' };
  });
  if (!newQr) return out;
  for (let i = 0; i < out.length; i += 1) {
    if (!codeOf(out[i])) out[i] = { ...out[i], code: nextTubeCode(newQr, out) };
  }
  return out;
}

/** The longest organism a banked tube can name (routes/slants.py's
 *  preserved_organism limit). */
export const ORGANISM_MAX = 300;

/**
 * A fresh bank-stock form: one tube, cryopreserved, in the -80 °C freezer,
 * preserved today by the person at the bench as the intake's taxon. The
 * organism follows the taxon name until someone edits it (IntakeForm).
 */
export const defaultBankStock = ({ qrUid = '', taxonName = '', userId = '', today = '' } = {}) => ({
  entries: [{ code: tubeCode(qrUid, 0), role: null, well: '' }],
  method: 'cryopreservation',
  protocol: '',
  protocolOther: '',
  unit: 'freezer_minus_80',
  unitLabel: '',
  rack: '',
  box: '',
  firstWell: '',
  preservedAt: today,
  preservedBy: userId,
  organism: taxonName,
});

/*
 * What a bank-stock intake must say, in the order BankStockFields shows it:
 * a tube code at least, no code twice, each well a well and no well twice,
 * a storage unit, and the preservation record every banked tube carries
 * (mushroom_bank_preservation_complete) -- dated no later than today.
 */
export function bankStockErrors(sub, today) {
  const coded = sub.entries.map((en) => en.code.trim());
  // Codes are compared without case, as the bank's labels are read.
  const seenCodes = new Set();
  let repeated = null;
  for (const code of coded.filter(Boolean)) {
    if (seenCodes.has(code.toLowerCase())) { repeated = code; break; }
    seenCodes.add(code.toLowerCase());
  }
  const seenWells = new Set();
  const wellErrors = sub.entries.map((en, i) => {
    const well = normaliseWell(en.well);
    if (!well || !en.code.trim()) return [`tubeWell${i}`, null];
    if (!isWell(well)) return [`tubeWell${i}`, { code: 'invalid', key: 'source.errors.wellFormat' }];
    if (seenWells.has(well)) return [`tubeWell${i}`, { code: 'duplicate', key: 'source.errors.duplicateWell', well }];
    seenWells.add(well);
    return [`tubeWell${i}`, null];
  });
  const preservedAt = required(sub.preservedAt, 'source.errors.preservedAtRequired')
    || (today && sub.preservedAt > today ? { code: 'tooLarge', key: 'slants:storage.errors.invalid_preserved_at' } : null);
  return collectErrors([
    ['slantCode', coded.some(Boolean) ? null : { code: 'required', key: 'intake.slantCodeRequiredError' }],
    ['slantCode', repeated ? { code: 'duplicate', key: 'source.errors.duplicateCode', tube: repeated } : null],
    ...wellErrors,
    ['unit', required(sub.unit, 'source.errors.unitRequired')],
    ['preservedAt', preservedAt],
    ['preservedBy', required(sub.preservedBy, 'source.errors.preservedByRequired')],
    ['organism', required(sub.organism, 'source.errors.organismRequired')],
    // The organism follows the taxon name, which has no length limit of its
    // own, so the input's maxLength alone does not hold it to the server's.
    ['organism', String(sub.organism ?? '').trim().length > ORGANISM_MAX
      ? { code: 'tooLong', key: 'source.errors.organismTooLong', max: ORGANISM_MAX } : null],
  ]);
}

/* ── validation and payloads for the whole intake ──────────────────────── */

/**
 * Every requirement of an intake of this type at once, keyed by field in
 * screen order (focus goes to the first): what arrived (its own section, just
 * under the cards), then a donor or an original collector (POST /external's
 * one server rule), then the specimen description -- all of it when it IS
 * the record, and for every type a fruiting-body count that is a count.
 * Taxon name stays optional: a donation can arrive unidentified.
 *
 * `sub` is the type's own section: { condition } for tissue, the plate form
 * for a culture, the bank-stock form for bank stock. `today` (YYYY-MM-DD,
 * local) bounds a preservation date.
 */
export function sourceErrors(type, form, sub, { today } = {}) {
  const source = SOURCES[type];
  if (!source) return { source: { code: 'required', key: 'source.required' } };
  const material = source.material === 'plate' ? Object.entries(plateSubErrors(sub, 'intake.plateProtocolRequiredError'))
    : source.material === 'bankStock' ? Object.entries(bankStockErrors(sub, today))
      : source.material === 'condition' ? [['condition', required(sub?.condition, 'source.errors.conditionRequired')]]
        : [];
  return collectErrors([
    ...material,
    ['donor_name', isBlankPair(form.donor_name, form.original_collector)
      ? { code: 'required', key: 'form.donorRequiredError' } : null],
    ...DESCRIPTION_FIELDS.map((f) => [f,
      (source.description === 'required' ? required(form[f]) : null)
        || (f === 'fruiting_body_count' ? countError(form[f]) : null)]),
  ]);
}

/** The isolate's preservation_method for this intake, or null for none. */
export function preservationMethodOf(type, sub) {
  if (type === 'tissue') return CONDITION_METHOD[sub?.condition] || null;
  if (type === 'bank_stock') return sub?.method || null;
  return null;
}

/**
 * POST /external's body: the intake record, what arrived (`donation_type`)
 * and, for a dried, silica-gel or banked specimen, how it is kept -- written
 * on the isolate the server mints. Blank optional values go as null, so an
 * ENUM column is never sent ''.
 */
export function externalBody(type, form, sub, user) {
  const method = preservationMethodOf(type, sub);
  return {
    ...form,
    donation_type: SOURCES[type].donationType,
    received_by: user?.user_id,
    original_gps_lat: form.original_gps_lat || null,
    original_gps_lng: form.original_gps_lng || null,
    original_collection_date: form.original_collection_date || null,
    fruiting_body_size: form.fruiting_body_size || null,
    phenological_stage: form.phenological_stage || null,
    fruiting_body_count: form.fruiting_body_count || null,
    growth_pattern: form.growth_pattern || null,
    substrate: form.substrate || null,
    ...(method ? { preservation_method: method } : {}),
  };
}

/**
 * POST /slants/external-intake's body for bank stock: the tubes with a code,
 * each with its own well when it has one; the place they share, its blank
 * parts left out; and the preservation record. The server places every tube
 * or registers none (routes/slants.py::create_external_slant_intake).
 */
export function bankStockBody(externalId, sub, user) {
  const text = (v) => String(v ?? '').trim();
  const place = { unit: sub.unit };
  [['unit_label', sub.unitLabel], ['rack', sub.rack], ['box', sub.box]].forEach(([key, value]) => {
    if (text(value)) place[key] = text(value);
  });
  const protocol = protocolOf(sub);
  return {
    external_id: externalId,
    slants: sub.entries.filter((en) => en.code.trim()).map((en) => {
      const well = normaliseWell(en.well);
      return { code: en.code.trim(), role: en.role || null, ...(well ? { well } : {}) };
    }),
    ...(protocol ? { protocol } : {}),
    place,
    preserved_at: sub.preservedAt,
    preserved_by: sub.preservedBy || user?.user_id,
    preserved_organism: text(sub.organism),
  };
}

/*
 * The bank-stock step's refusals, each the translation key it is said with:
 * the server names them by `code` (routes/slants.py _tube_storage,
 * _apply_storage and create_external_slant_intake). One it has no code for
 * keeps the server's own words, and one with no words either says so.
 */
const BANK_STEP_KEYS = {
  code_taken: 'source.errors.codeTaken',
  duplicate_code: 'source.errors.duplicateCode',
  busy: 'source.errors.busy',
  well_occupied: 'source.errors.wellOccupied',
  duplicate_well: 'source.errors.duplicateWell',
  invalid_place: 'source.errors.invalidPlace',
  preservation_incomplete: 'slants:storage.errors.preservation_incomplete',
  specimen_code_required: 'slants:storage.errors.specimen_code_required',
  invalid_storage: 'slants:storage.errors.invalid_storage',
  invalid_text: 'slants:storage.errors.invalid_text',
  invalid_preserved_at: 'slants:storage.errors.invalid_preserved_at',
  unknown_technician: 'slants:storage.errors.unknown_technician',
};

/** A bank-stock refusal in the reader's language (`t` is the form's), or the
 *  server's own words for one with no code -- never nothing. */
export function bankStepMessage(data, t) {
  const key = BANK_STEP_KEYS[data?.code];
  if (!key) return data?.error || t('source.errors.stepUnknown');
  return t(key, {
    holder: data.holder,
    tube: data.slant_code,
    well: data.well,
    codes: (Array.isArray(data.codes) ? data.codes : []).join(', '),
    fields: (data.missing || []).map((f) => t(`slants:storage.missing.${f}`)).join(', '),
  });
}

/**
 * Why a step after POST /external failed, as the stepFailed sentences say it:
 * a request that never reached the server (no response at all) says so; a
 * bank-stock refusal is bankStepMessage's; any other is the server's words,
 * or that it gave none. Never empty.
 */
export function stepFailureReason(err, t, { bank = false } = {}) {
  const res = err?.response;
  if (!res) return t('source.errors.unreachable');
  if (bank) return bankStepMessage(res.data, t);
  return res.data?.error || t('source.errors.stepUnknown');
}
